import { useCallback, useEffect, useRef, useState } from 'react'

import type { PasskeySummary } from '../services/account/authApi'
import { addPasskey, deletePasskey, fetchPasskeys } from '../services/account/authService'

export type PasskeysStatus = 'loading' | 'ready' | 'error'

interface UsePasskeysResult {
  /** Null until the first load finishes. */
  passkeys: PasskeySummary[] | null
  status: PasskeysStatus
  /** Saves a passkey on this device. Resolves to false if the player dismissed the prompt; rejects on a real failure. */
  add: (nickname?: string) => Promise<boolean>
  /** Removes a passkey. Rejects on failure (for example AUTH_REAUTH_REQUIRED). */
  remove: (id: string) => Promise<void>
  reload: () => void
}

/**
 * The signed-in player's passkeys. Pass `enabled: false` (passkeys off, or not
 * signed in) and nothing is fetched.
 */
export const usePasskeys = (enabled: boolean): UsePasskeysResult => {
  const [passkeys, setPasskeys] = useState<PasskeySummary[] | null>(null)
  const [status, setStatus] = useState<PasskeysStatus>('loading')
  const [attempt, setAttempt] = useState(0)
  // Only the newest load may write its answer.
  const requestId = useRef(0)

  useEffect(() => {
    if (!enabled) return
    const id = ++requestId.current
    setStatus('loading')
    fetchPasskeys()
      .then((list) => {
        if (id !== requestId.current) return
        setPasskeys(list)
        setStatus('ready')
      })
      .catch(() => {
        if (id !== requestId.current) return
        setStatus('error')
      })
    return () => {
      // Invalidate an in-flight load when disabled or unmounted.
      requestId.current += 1
    }
  }, [enabled, attempt])

  const add = useCallback(async (nickname?: string) => {
    const created = await addPasskey(nickname)
    if (!created) return false
    setPasskeys((current) => [...(current ?? []), created])
    setStatus('ready')
    return true
  }, [])

  const remove = useCallback(async (id: string) => {
    await deletePasskey(id)
    setPasskeys((current) => (current ?? []).filter((p) => p.id !== id))
  }, [])

  const reload = useCallback(() => setAttempt((n) => n + 1), [])

  return { passkeys, status, add, remove, reload }
}
